import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AUTHENTICATION_SERVICE_TOKEN,
  AuthenticationStepEnum,
  Credentials,
  ENVIRONMENT_TOKEN,
  ICONS,
  LoginRequest,
  LoginResult,
  UTILS,
  USER_ROLE_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import { IconComponent, ToasterService } from '@aksia/ui';
import { Observable } from 'rxjs';
import { isValidReturnUrl } from '../../utils/return-url.utils';
import {
  HttpHeaders,
  HttpClient,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';

/** Which activation screen the account state calls for. */
type ActivationKind = 'setup' | 'reset';

/** Outcome of the background request that mails the activation link. */
type ActivationLinkState = 'sent' | 'throttled' | 'failed';

@Component({
  selector: 'app-login',
  imports: [IconComponent],
  templateUrl: './login.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPageContent {
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly auth = inject(AUTHENTICATION_SERVICE_TOKEN);
  protected readonly userService = inject(USER_ROLE_SERVICE_TOKEN);

  protected readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly httpClient = inject(HttpClient);
  private readonly toaster = inject(ToasterService);
  @HostBinding('class')
  uiclass = 'auth-page';

  protected readonly ICONS = ICONS;

  readonly authStepEnum = AuthenticationStepEnum;
  readonly version = this.environment.version;
  readonly currentYear = new Date().getFullYear();
  readonly credentials = signal(new Credentials());
  readonly rememberMe = signal(true);
  readonly submitted = signal(false);
  readonly inFlight = signal(false);
  readonly usernameTouched = signal(false);
  readonly passwordTouched = signal(false);
  readonly formShaking = signal(false);

  /** Set when the account cannot sign in until a password is set or updated. */
  readonly activationKind = signal<ActivationKind | undefined>(undefined);
  readonly activationEmail = signal<string | undefined>(undefined);
  readonly activationLinkState = signal<ActivationLinkState | undefined>(
    undefined,
  );
  readonly sendingActivationLink = signal(false);

  /** Mirrors LoginSecurity:ResetPasswordTokenValidMinutes on the identity server. */
  readonly resetLinkValidMinutes =
    this.environment.reset_password_token_valid_minutes;

  constructor() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe((paramMap) => {
        const rawUrl = paramMap.get('returnUrl') ?? paramMap.get('redirectUrl');

        if (!isValidReturnUrl(rawUrl)) {
          if (!this.auth.redirectUrl())
            this.router.navigate(['/session-required']);
          return;
        }

        this.auth.redirectUrl.set(() =>
          rawUrl!.replace(
            /authenticated=[^&]*/,
            `authenticated=${this.credentials().maxusername}`,
          ),
        );
      });
  }

  readonly isTwoFactorStep = computed(() =>
    [
      AuthenticationStepEnum.Requested2FA,
      AuthenticationStepEnum.Verifying2FA,
    ].includes(this.auth.authenticationStep()),
  );

  readonly title = computed(() => {
    switch (this.auth.authenticationStep()) {
      case AuthenticationStepEnum.IsAuthenticated:
        return 'Welcome';
      case AuthenticationStepEnum.Verifying2FA:
      case AuthenticationStepEnum.Requested2FA:
        return 'Verify your access';
      default:
        return 'Sign in';
    }
  });

  readonly btnTitle = computed(() => {
    switch (this.auth.authenticationStep()) {
      case AuthenticationStepEnum.Requested2FA:
        return 'Verify code';
      case AuthenticationStepEnum.Verifying2FA:
        return 'Verifying...';
      case AuthenticationStepEnum.VerifyingCredentials:
        return 'Signing in...';
      default:
        return 'Sign in';
    }
  });

  readonly signInIsDisabled = computed(() => {
    if (
      this.inFlight() ||
      this.auth.authenticationStep() ===
        AuthenticationStepEnum.VerifyingCredentials ||
      this.auth.authenticationStep() === AuthenticationStepEnum.Verifying2FA
    ) {
      return true;
    }

    // if (this.isTwoFactorStep()) {
    //   return UTILS.OBJECT.isEmpty(this.credentials().IPVerificationCode);
    // }

    return (
      UTILS.OBJECT.isEmpty(this.credentials().maxusername) ||
      UTILS.OBJECT.isEmpty(this.credentials().maxpassword)
    );
  });

  readonly showLoader = computed(
    () =>
      this.inFlight() ||
      this.auth.authenticationStep() ===
        AuthenticationStepEnum.VerifyingCredentials ||
      this.auth.authenticationStep() === AuthenticationStepEnum.Verifying2FA,
  );

  protected updateUsername(value: string) {
    this.credentials.set({
      ...this.credentials(),
      ...{ maxusername: value },
    });
  }

  protected updatePassword(value: string) {
    this.credentials.set({
      ...this.credentials(),
      ...{ maxpassword: value },
    });
  }

  protected updateVerificationCode(value: string) {
    this.credentials.set({
      ...this.credentials(),
      ...{ IPVerificationCode: value.replace(/\D/g, '') as never },
    });
  }

  protected showFieldError(field: 'username' | 'password' | 'code') {
    switch (field) {
      case 'username':
        return (
          (this.submitted() || this.usernameTouched()) &&
          UTILS.OBJECT.isEmpty(this.credentials().maxusername)
        );
      case 'password':
        return (
          (this.submitted() || this.passwordTouched()) &&
          UTILS.OBJECT.isEmpty(this.credentials().maxpassword)
        );
      case 'code':
        return (
          this.isTwoFactorStep() &&
          this.submitted() &&
          UTILS.OBJECT.isEmpty(this.credentials().IPVerificationCode)
        );
    }
  }

  passwordVisibility = signal(false);

  protected submit(event?: Event) {
    event?.preventDefault();
    this.submitted.set(true);
    const redirectUrl = this.resolveReturnUrl();

    const loginrequest: LoginRequest = {
      userName: this.credentials().maxusername,
      password: this.credentials().maxpassword,
      rememberMe: this.rememberMe(),
      passthroughParams: { returnUrl: redirectUrl || '' },
    };

    if (this.signInIsDisabled()) {
      const hasEmptyField =
        UTILS.OBJECT.isEmpty(this.credentials().maxusername) ||
        UTILS.OBJECT.isEmpty(this.credentials().maxpassword);
      if (hasEmptyField) this.triggerShake();
      return;
    }

    this.auth.authenticationStep() === this.authStepEnum.Requested2FA
      ? this.auth.verify2FA?.(this.credentials())
      : // : this.auth.signIn(this.credentials());
        (this.inFlight.set(true),
        this.login(loginrequest).subscribe({
          next: (res) => {
            this.userService.redirectAfterLogin?.set(undefined);
            if (res.requiresSecondFactorEnrolment) {
              this.inFlight.set(false);
              this.router.navigate(['/setup-authenticator'], {
                queryParams: { returnUrl: redirectUrl },
              });
              return;
            }
            if (res.requiresSecondFactor || res.requiresLocationVerification) {
              this.inFlight.set(false);
              this.router.navigate(['/verify-location'], {
                queryParams: {
                  returnUrl: redirectUrl,
                  methods: res.methods?.join(','),
                  method: res.defaultMethod,
                  to: res.maskedTarget ?? undefined,
                },
              });
              return;
            }
            const returnUrl = redirectUrl ?? res.redirectUrl;
            document.location.href = `${this.environment.microservices_api_url}${returnUrl ?? '/home'}`;
          },
          error: (err: HttpErrorResponse) => {
            this.inFlight.set(false);
            if (err.status === 429) {
              this.toaster.error(
                'Too many sign-in attempts. Please try again later.',
              );
            } else if (err.error?.error === 'password_setup_required') {
              this.enterActivation('setup', err.error?.masked_email);
            } else if (err.error?.error === 'password_reset_required') {
              this.enterActivation('reset', err.error?.masked_email);
            } else if (
              err.error?.error === 'invalid_grant' &&
              err.error?.error_description === 'Your account is locked.'
            ) {
              this.toaster.error(
                'Your account is locked. Use "Forgot password" to reset your credentials.',
              );
            } else if (err.error?.error === 'invalid_grant') {
              this.toaster.error('The username or password is invalid.');
            } else if (err.error?.error === 'access_denied') {
              this.toaster.error(
                'Access denied. You do not have permission to sign in.',
              );
            } else {
              const correlationId = err.error?.correlation_id as
                string | undefined;
              this.toaster.error(
                correlationId
                  ? `Login failed. Please contact support. Reference: ${correlationId}`
                  : 'Login failed. Please contact support.',
              );
            }
          },
        }));
  }

  private triggerShake() {
    this.formShaking.set(true);
    setTimeout(() => this.formShaking.set(false), 400);
  }

  /**
   * Resolves the OAuth return url for this attempt, preferring the one captured from the
   * query string over the one the user service remembered.
   */
  private resolveReturnUrl(): string | undefined {
    const storedRedirect = this.auth.redirectUrl();
    const redirectUrl =
      typeof storedRedirect === 'function'
        ? storedRedirect()
        : storedRedirect?.toString();

    return (
      redirectUrl ||
      (this.userService?.redirectAfterLogin?.() as string | undefined)
    );
  }

  /**
   * Switches the form over to the activation screen and requests the link straight away,
   * so the user only ever has to click once.
   */
  private enterActivation(kind: ActivationKind, maskedEmail?: string) {
    this.activationKind.set(kind);
    this.activationEmail.set(maskedEmail);
    this.activationLinkState.set(undefined);
    this.sendActivationLink();
  }

  /**
   * Requests the password link through /account/forgot-password rather than having the login
   * endpoint send it, so the send stays on the stricter password-reset rate limiter.
   */
  protected sendActivationLink() {
    if (this.sendingActivationLink()) return;

    this.sendingActivationLink.set(true);

    const url = `${this.environment.microservices_api_url}/account/forgot-password`;
    const returnUrl = this.resolveReturnUrl();
    let body = new HttpParams().set(
      'username',
      this.credentials().maxusername ?? '',
    );

    if (returnUrl) body = body.set('returnUrl', returnUrl);

    this.httpClient
      .post(url, body.toString(), { headers: this.formUrlEncodedHeaders })
      .subscribe({
        next: () => {
          this.sendingActivationLink.set(false);
          this.activationLinkState.set('sent');
        },
        error: (err: HttpErrorResponse) => {
          this.sendingActivationLink.set(false);
          if (err.status === 429) {
            this.activationLinkState.set('throttled');
          } else if (err.status >= 500) {
            this.activationLinkState.set('failed');
          } else {
            // The endpoint answers 200 for anything it will not disclose; treat the
            // remaining 4xx the same way rather than implying the account is missing.
            this.activationLinkState.set('sent');
          }
        },
      });
  }

  protected leaveActivation() {
    this.activationKind.set(undefined);
    this.activationEmail.set(undefined);
    this.activationLinkState.set(undefined);
    this.submitted.set(false);
    this.passwordTouched.set(false);
    this.updatePassword('');
  }

  protected goToForgotPassword() {
    const returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') ??
      this.route.snapshot.queryParamMap.get('redirectUrl');
    this.router.navigate(
      ['/forgot-password'],
      returnUrl ? { queryParams: { returnUrl } } : {},
    );
  }

  private readonly formUrlEncodedHeaders = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  login(request: LoginRequest): Observable<LoginResult> {
    const returnUrl = request.passthroughParams?.['returnUrl'];
    const queryParams = returnUrl
      ? `?returnUrl=${encodeURIComponent(Array.isArray(returnUrl) ? returnUrl[0] : returnUrl)}`
      : '';
    const url = `${this.environment.microservices_api_url}/account/login${queryParams}`;

    const body = new HttpParams()
      .set('username', request.userName)
      .set('password', request.password)
      .set('rememberMe', request.rememberMe);

    return this.httpClient.post<LoginResult>(url, body.toString(), {
      headers: this.formUrlEncodedHeaders,
      withCredentials: true,
    });
  }
}
